/* eslint-disable no-bitwise */
import { invariant } from '../util/invariant';
import { BitReader } from './bitReader';
import {
  AAC_LOW_COMPLEXITY_PROFILE, AAC_MAIN_PROFILE, AAC_RESERVED_PROFILE, AAC_SCALABLE_SAMPLING_RATE_PROFILE,
  samplingFrequencyInHz,
} from './data';
import { AacDecoder } from './aacDecoder';
import type { DecodedPcmFloat32Channels } from './types';



/**
 * Signed 16-bits PCM samples gathered in a single buffer
 * ready to be written to .raw file.
 */
interface DecodedPcmS16leSamples {
  samples: Uint8Array;
  numberOfChannels: number;
  sampleRateInHz: number;
}


export function decodeAacAsPcmS16le(adtsStreamBuffer: Uint8Array): DecodedPcmS16leSamples {
  return new AdtsStreamParser(adtsStreamBuffer).decodePcmS16leSamples();
}


export function decodeAacAsPcmChannels(adtsStreamBuffer: Uint8Array): DecodedPcmFloat32Channels {
  return new AdtsStreamParser(adtsStreamBuffer).decodePcmFloat32Channels();
}


/**
 * This class implements a decoder for AAC Low Complexity that,
 * given a buffer containing an ADTS stream, will provide the
 * corresponding raw PCM S16LE samples.
 *
 *
 * ----------------------
 * ADTS decoding overview
 * ----------------------
 *
 * Within mp4, AAC data is wrapped in an ADTS stream.
 *
 * An ADTS stream is made of ADTS frames.
 *
 * An ADTS frame is made of an ADTS header containing some information
 * like the AAC profile or the channel layout, followed by raw data blocks
 * that represent each an AAC frame.
 */
export class AdtsStreamParser {

  private aacDecoder: AacDecoder | undefined;
  private bitReader: BitReader;

  private aac_profile: number | undefined;
  private sampling_frequency_index: number | undefined;
  private channel_configuration: number | undefined;
  private numberOfChannels: number | undefined;
  private emitPCMs16LESamples = true;

  private onlyProbe = false;


  constructor(buffer: Uint8Array) {
    this.bitReader = new BitReader(buffer);
  }

  decodePacket(packet: Uint8Array): DecodedPcmFloat32Channels {
    invariant(!this.bitReader.hasMoreData(), 'Should not have data in bit reader');
    this.bitReader = new BitReader(packet);

    return this.decodePcmFloat32Channels();
  }


  private decodeSamples(): void {
    while (this.bitReader.hasMoreData()) {
      this.adts_frame();
    }
  }


  decodePcmS16leSamples(): DecodedPcmS16leSamples {
    this.decodeSamples();
    const samples = this.aacDecoder === undefined ? new Uint8Array(0) : this.aacDecoder.getSamples();
    const numberOfChannels = this.numberOfChannels;
    invariant(numberOfChannels !== undefined, 'Number of channels should be defined');
    invariant(this.sampling_frequency_index !== undefined, 'Sample frequence index should be defined');
    const sampleRateInHz = samplingFrequencyInHz[this.sampling_frequency_index]!;
    return {
      samples,
      numberOfChannels,
      sampleRateInHz,
    };
  }


  decodePcmFloat32Channels(): DecodedPcmFloat32Channels {
    this.emitPCMs16LESamples = false;
    this.aacDecoder?.clearPcmSamples(this.emitPCMs16LESamples);
    this.decodeSamples();
    const numberOfChannels = this.numberOfChannels;
    invariant(numberOfChannels !== undefined, 'Number of channels should be defined');
    invariant(this.sampling_frequency_index !== undefined, 'Sample frequence index should be defined');
    const sampleRateInHz = samplingFrequencyInHz[this.sampling_frequency_index]!;

    const decodedChannels = this.aacDecoder === undefined ? [] : this.aacDecoder.getChannels();

    const channels: Float32Array[] = [];
    for (const channel of decodedChannels) {
      let nSamples = 0;
      for (const sampleArray of channel) {
        nSamples += sampleArray.length;
      }
      const allSamples = new Float32Array(nSamples);
      let offset = 0;
      for (const sampleArray of channel) {
        allSamples.set(sampleArray, offset);
        offset += sampleArray.length;
      }

      channels.push(allSamples);
    }
    return {
      sampleRateInHz,
      channels,
    };
  }


  private adts_frame() {
    // adts_fixed_header
    const syncword = this.bitReader.readBits(12);
    if (syncword !== 0xFFF) {
      throw new Error(`Invalid syncword: ${syncword}`);
    }

    // ID
    this.bitReader.readBits(1);

    // layer
    this.bitReader.readBits(2);

    const protection_absent = this.bitReader.readBits(1);

    // profile
    const aac_profile = this.bitReader.readBits(2);
    if (this.aac_profile !== undefined) {
      if (this.aac_profile !== aac_profile) {
        throw new Error(`Inconsistant aac profile values: previous = ${this.aac_profile}, current = ${aac_profile}`);
      }
    } else {
      this.aac_profile = aac_profile;
    }

    const sampling_frequency_index = this.bitReader.readBits(4);
    if (sampling_frequency_index >= samplingFrequencyInHz.length) {
      if (syncword !== 0xFFF) {
        throw new Error(`Invalid sampling frequency index: ${sampling_frequency_index}`);
      }
    }

    if (this.sampling_frequency_index !== undefined) {
      if (this.sampling_frequency_index !== sampling_frequency_index) {
        throw new Error(`Inconsistant sample frequency index values: previous = ${this.sampling_frequency_index}, current = ${sampling_frequency_index}`);
      }
    } else {
      this.sampling_frequency_index = sampling_frequency_index;
    }

    // private_bit
    this.bitReader.readBits(1);

    const channel_configuration = this.bitReader.readBits(3);

    if (this.channel_configuration !== undefined) {
      if (this.channel_configuration !== channel_configuration) {
        throw new Error(`Inconsistent channel configuration values: previous = ${this.channel_configuration}, current = ${channel_configuration}`);
      }
    } else {
      this.channel_configuration = channel_configuration;
    }

    // original_copy
    this.bitReader.readBits(1);

    // home
    this.bitReader.readBits(1);

    // adts_variable_header

    // copyright_identification_bit
    this.bitReader.readBits(1);

    // copyright_identification_start
    this.bitReader.readBits(1);

    // aac_frame_length
    this.bitReader.readBits(13);

    // adts_buffer_fullness
    this.bitReader.readBits(11);

    if (!this.onlyProbe) {
      switch (aac_profile) {
        case AAC_MAIN_PROFILE: throw 'AAC_MAIN_PROFILE not supported';
        case AAC_LOW_COMPLEXITY_PROFILE: break;
        case AAC_SCALABLE_SAMPLING_RATE_PROFILE: throw 'AAC_SCALABLE_SAMPLING_RATE_PROFILE not supported';
        case AAC_RESERVED_PROFILE: throw 'Unexpected AAC_RESERVED_PROFILE';
      }
    }

    if (this.onlyProbe && channel_configuration > 0) {
      // If we only want to know the channel count and the channel configuration is > 0,
      // then we have the answer without having to parse anything else. Otherwise,
      // we have either an implicit channel mapping or an explicit one, and we need to decode
      // the frame elements to get the answer (which may fail if the profile is not one we support)
      this.numberOfChannels = channel_configuration === 7 ? 8 : this.channel_configuration;
      return;
    }

    const number_of_raw_data_blocks_in_frame = this.bitReader.readBits(2);

    if (this.aacDecoder === undefined) {
      this.aacDecoder = new AacDecoder(sampling_frequency_index, channel_configuration, this.emitPCMs16LESamples);
    }

    if (number_of_raw_data_blocks_in_frame === 0) {
      this.adts_error_check(protection_absent);
      const n = this.aacDecoder.decodeAacFrame(this.bitReader, this.onlyProbe);
      if (this.onlyProbe) {
        return n;
      }
      this.numberOfChannels = n;
    } else {
      this.adts_header_error_check(protection_absent, number_of_raw_data_blocks_in_frame);
      for (let i = 0; i <= number_of_raw_data_blocks_in_frame; i++) {
        const n = this.aacDecoder.decodeAacFrame(this.bitReader, this.onlyProbe);
        if (this.onlyProbe) {
          return n;
        }
        this.numberOfChannels = n;
        this.adts_raw_data_block_error_check(protection_absent);
      }
    }
  }


  private adts_error_check(protection_absent: number): void {
    if (protection_absent === 0) {
      // crc_check
      this.bitReader.readBits(16);
    }
  }


  private adts_raw_data_block_error_check(protection_absent: number) {
    if (protection_absent === 0) {
      // crc_check
      this.bitReader.readBits(16);
    }
  }

  private adts_header_error_check(protection_absent: number, number_of_raw_data_blocks_in_frame: number): number[] {
    const raw_data_block_position: number[] = [];

    if (protection_absent === 0) {
      for (let i = 0; i < number_of_raw_data_blocks_in_frame; i++) {
        raw_data_block_position[i] = this.bitReader.readBits(16);
      }

      // crc_check
      this.bitReader.readBits(16);
    }

    return raw_data_block_position;
  }

}

